import React, { useState, useRef } from 'react';
import { 
  CloudArrowUpIcon, 
  PhotoIcon, 
  XMarkIcon,
  PlusIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import { imageUploadService } from '../../services/imageUploadService';
import { useTheme } from '../../contexts/ThemeContext';

interface MediaUploaderProps {
  value: string | string[];
  onChange: (value: string | string[]) => void;
  multiple?: boolean;
  category?: string;
  label?: string;
}

const AdminMediaUploader: React.FC<MediaUploaderProps> = ({
  value,
  onChange,
  multiple = false,
  category = 'accommodation',
  label
}) => {
  const { actualTheme } = useTheme();
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const images = Array.isArray(value) ? value : (value ? [value] : []);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    setProgress(0);

    try {
      if (multiple) {
        const fileArray = Array.from(files);
        const results = await imageUploadService.uploadMultipleImages(
          fileArray,
          category,
          (fileIndex, prog) => {
            // Simple average progress
            const totalProgress = (fileIndex * 100 + prog.percentage) / fileArray.length;
            setProgress(Math.round(totalProgress));
          }
        );
        const newUrls = results.map(r => r.url);
        onChange([...images, ...newUrls]);
      } else {
        const result = await imageUploadService.uploadImage(
          files[0],
          category,
          (prog) => setProgress(prog.percentage)
        );
        onChange(result.url);
      }
    } catch (error) {
      console.error('Upload failed:', error);
      alert('Upload failed. Please try again.');
    } finally {
      setUploading(false);
      setProgress(0);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removeImage = (urlToRemove: string) => {
    if (multiple) {
      onChange(images.filter(url => url !== urlToRemove));
    } else {
      onChange('');
    }
  };

  return (
    <div className="space-y-4">
      {label && (
        <label className={`block text-sm font-medium mb-2 ${
          actualTheme === 'dark' ? 'text-slate-300' : 'text-neutral-700'
        }`}>
          {label}
        </label>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
        {/* Existing Images */}
        {images.map((url, idx) => (
          <div 
            key={`${url}-${idx}`} 
            className="group relative aspect-square rounded-xl overflow-hidden border border-neutral-200 dark:border-slate-700 shadow-sm bg-neutral-100 dark:bg-slate-800"
          >
            <img 
              src={url} 
              alt={`Upload ${idx}`} 
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
            />
            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
              <button
                type="button"
                onClick={() => removeImage(url)}
                className="p-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors shadow-lg"
                title="Remove image"
              >
                <TrashIcon className="w-4 h-4" />
              </button>
            </div>
            {idx === 0 && !multiple && (
              <div className="absolute top-2 left-2 px-2 py-0.5 bg-primary-500 text-white text-[10px] font-bold rounded-md shadow-sm">
                PRIMARY
              </div>
            )}
          </div>
        ))}

        {/* Upload Button */}
        {(multiple || images.length === 0) && (
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className={`aspect-square rounded-xl border-2 border-dashed flex flex-col items-center justify-center gap-2 transition-all duration-300 group relative overflow-hidden ${
              uploading 
                ? 'border-primary-400 bg-primary-50/10' 
                : actualTheme === 'dark'
                  ? 'border-slate-700 hover:border-primary-500 hover:bg-slate-800'
                  : 'border-neutral-200 hover:border-primary-500 hover:bg-neutral-50'
            }`}
          >
            {uploading ? (
              <div className="flex flex-col items-center gap-3 w-full px-4 text-center">
                <div className="relative w-12 h-12">
                  <svg className="w-full h-full animate-spin text-primary-500" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center text-[10px] font-bold text-primary-600">
                    {progress}%
                  </div>
                </div>
                <span className="text-xs font-semibold text-primary-600">Uploading...</span>
              </div>
            ) : (
              <>
                <div className={`p-3 rounded-full transition-colors ${
                  actualTheme === 'dark' ? 'bg-slate-700 text-slate-400 group-hover:bg-primary-500/10 group-hover:text-primary-500' : 'bg-neutral-100 text-neutral-400 group-hover:bg-primary-50 group-hover:text-primary-500'
                }`}>
                  <CloudArrowUpIcon className="w-6 h-6" />
                </div>
                <div className="text-center">
                  <span className={`block text-xs font-bold ${
                    actualTheme === 'dark' ? 'text-slate-400' : 'text-neutral-500'
                  }`}>
                    {multiple ? 'Add Images' : 'Upload Image'}
                  </span>
                  <span className="text-[10px] text-neutral-400">JPG, PNG up to 10MB</span>
                </div>
              </>
            )}
            
            {uploading && (
              <div 
                className="absolute bottom-0 left-0 h-1 bg-primary-500 transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            )}
          </button>
        )}
      </div>

      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        multiple={multiple}
        accept="image/*"
        className="hidden"
      />
    </div>
  );
};

export default AdminMediaUploader;
